import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  Renderer2,
  ViewChild,
  signal,
} from '@angular/core';
import { PanelMenuModule } from 'primeng/panelmenu';
import { MenuItem } from 'primeng/api';
import { AsyncPipe, NgClass } from '@angular/common';
import { BadgeModule } from 'primeng/badge';
import { AuthorizationModule, RouteNavigateDirective } from 'authorization';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BreakpointObserver } from '@angular/cdk/layout';
import { JwtAuthTokenService } from 'angular-jwt-auth';
import { filter, map, Observable, switchMap } from 'rxjs';
import { UserService } from 'src/domains/share/app/service/user/user.service';
import { AppUserModel } from 'src/domains/authentication/model/app-user/app-user.model';
import { BaseButtons, ButtonsLoaderModule } from 'buttons-loader';
import { LoginRequest } from 'src/domains/authentication/model/login-auth/login.request';
import { AppUserRequest } from 'src/domains/authentication/model/app-user/app-user.request';
import { ImageOptimizerDirective } from 'lazy-load-image';
import { menu } from 'app/menu-ts/menu';

@UntilDestroy()
@Component({
  selector: 'app-vertical-menu',
  templateUrl: './app-vertical-menu.component.html',
  styleUrls: ['./app-vertical-menu.component.scss'],
  imports: [
    NgClass,
    AsyncPipe,
    PanelMenuModule,
    BadgeModule,
    RouteNavigateDirective,
    AuthorizationModule,
    ButtonsLoaderModule,
    ImageOptimizerDirective,
  ],
})
@UntilDestroy()
export class AppVerticalMenuComponent implements OnInit, AfterViewInit {
  @ViewChild('verticalMenu') menuRef: ElementRef<HTMLDivElement>;
  readonly model = signal<MenuItem[]>(menu);
  logoutBtn: BaseButtons<Observable<boolean>>;
  isAuthenticated$ = JwtAuthTokenService.isAuthenticated$.pipe(
    untilDestroyed(this),
  );

  readonly isMobileSize = signal<boolean>(false);
  userData: Observable<AppUserModel.Full>;
  constructor(
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private renderer2: Renderer2,
    private userService: UserService,
  ) {
    this.userData = JwtAuthTokenService.isAuthenticated$.pipe(
      filter((isAuthen) => !!isAuthen),
      switchMap(() => {
        const id = this.userService.getUserDetail()?.id;
        return new AppUserRequest.GetById({ id }).call();
      }),
      map((res) => res?.response?.data?.[0]),
      untilDestroyed(this),
    );
  }

  ngOnInit() {
    this.checkVisibility();
    // this.logoutBtn = {
    //   label: 'خروج',
    //   class: 'btn mx-1 text-muted d-flex align-items-center',
    //   action: () => {
    //     new LoginWithOtpRequest.Delete(undefined)
    //       .call()
    //       .pipe(untilDestroyed(this))
    //       .subscribe((data) => {
    //         if (data?.response?.statusCode === 200) {
    //           this.jwtAuthTokenService.clearToken();
    //           location.href = '/';
    //         }
    //       });
    //   },
    //   visibleCondition: this.isAuthenticated$,
    // };
    if (!this.model().some((item) => item.label === 'خروج')) {
      this.model.update((current) => {
        current.push({
          label: 'خروج',
          icon: 'isax icon-logout',
          command: () => {
            new LoginRequest.Delete(undefined)
              .call()
              .pipe(untilDestroyed(this))
              .subscribe(async (data) => {
                if (data?.response?.statusCode === 200) {
                  await JwtAuthTokenService.clearToken();
                  location.href = '/';
                }
              });
          },
        });
        return [...current];
      });
    }

    this.expandedMenu(this.router.url, this.model(), null);
    this.model.set([...this.model()]);

    this.router.events.pipe(untilDestroyed(this)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.expandedMenu(event.url, this.model(), null);
        this.model.set([...this.model()]);
      }
    });

    this.userService
      .getNotificationCount()
      .pipe(untilDestroyed(this))
      .subscribe((count) => {
        const notificationMenuItem = this.model().find(
          (item) => item.label === 'پیام ها',
        );
        if (notificationMenuItem) {
          notificationMenuItem.badge = count > 0 ? count.toString() : '';
          this.model.set([...this.model()]);
        }
      });
  }

  ngAfterViewInit() {
    this.breakpointObserver
      .observe(['(max-width: 991px)'])
      .pipe(
        untilDestroyed(this),
        map((value) => {
          if (value.matches) {
            requestAnimationFrame(() => {
              const headerTopbar: HTMLElement =
                this.renderer2.selectRootElement('.header__topbar', true);
              headerTopbar.style.display = 'none';

              const header: HTMLElement = this.renderer2.selectRootElement(
                '.header__navbar',
                true,
              );

              this.menuRef.nativeElement.style.marginBottom =
                header.offsetHeight + 'px';
            });
          }

          this.isMobileSize.set(value.matches);
        }),
      )
      .subscribe();
  }
  checkVisibility() {
    const bindVisibility = (items: MenuItem[]) => {
      for (const item of items) {
        if (item.visibility$) {
          item.visibility$.pipe(untilDestroyed(this)).subscribe((visible) => {
            item.visible = visible;
            item.isVisible = visible;
            this.model.set([...this.model()]);
          });
        }
        if (item.items?.length) {
          bindVisibility(item.items);
        }
      }
    };
    bindVisibility(this.model());
  }

  expandedMenu(url: string, menuItems: MenuItem[], root?: MenuItem): boolean {
    let hasActiveChild = false;

    for (let item of menuItems) {
      const isActive =
        url.includes(item?.routerLink?.path) ||
        (item.items && this.expandedMenu(url, item.items, item));
      if (isActive) {
        hasActiveChild = true;
      }
    }

    if (root) {
      root.expanded = hasActiveChild; // Expand or collapse the parent based on children
    }

    return hasActiveChild;
  }

  updateMenuVisibility(updatedItem: MenuItem, visibility: boolean) {
    // Update the item's visibility
    updatedItem.isVisible = visibility;

    // Find the item's parent and update its visibility accordingly
    this.updateParentVisibility(this.model());
    this.model.set([...this.model()]);
  }

  updateParentVisibility(menuItems: MenuItem[]) {
    for (const item of menuItems) {
      if (item.items?.length) {
        // Update the parent’s visibility
        item.isVisible = item.items.some((child) => child.isVisible !== false);

        // Recursively update higher-level parents
        this.updateParentVisibility(item.items);
      }
    }
  }
}
